/// <reference lib="dom" />
/** @jsx createElement */
import { createElement } from "preact";
import { clamp } from "@utils/utils";
import { ParamDict, ParamDictToProps } from "@utils/params";
import {
  FreqResponseChart,
  FreqResponseHandleConfig,
  initFreqResponseChart,
  updateFreqResponseChart,
} from "./FreqResponseChart.ts";
import {
  buildResponsePath,
  FreqSvgOpts,
  FreqSvgOptsDefaults,
  makeFt,
  makeTx,
  qToY,
  yToQ,
} from "../FreqSvg.tsx";
import { useD3Chart } from "../d3Chart.ts";

// Moog cutoff is normalized 0–1. Map to Hz for frequency axis visualization.
// cutoff=0.065 (default) → ~1300Hz; cutoff=1 → 20000Hz.
function cutoffToHz(cutoff: number, freqOpts: FreqSvgOpts): number {
  return clamp(
    cutoff * freqOpts["domain/fMax"],
    freqOpts["domain/fMin"],
    freqOpts["domain/fMax"],
  );
}
function hzToCutoff(
  freqHz: number,
  freqOpts: FreqSvgOpts,
  cutoffParam: { min: number; max: number },
): number {
  return clamp(freqHz / freqOpts["domain/fMax"], cutoffParam.min, cutoffParam.max);
}

//#region ResonanceQParams
// Resonance mapped to Q 0.5–10.5 for visualization.

export const ResonanceQParams = {
  "domain/qMin": { type: "float", value: 0.5 },
  "domain/qMax": { type: "float", value: 10.5 },
} satisfies ParamDict;
export type ResonanceQParams = typeof ResonanceQParams;

export const ResonanceQOptsDefaults = ParamDictToProps(ResonanceQParams);
export type ResonanceQOpts = typeof ResonanceQOptsDefaults;

//#endregion ResonanceQParams

function resonanceToQ(
  resonance: number,
  resonanceOpts: ResonanceQOpts,
  resonanceParam: { max: number },
): number {
  const qMin = resonanceOpts["domain/qMin"];
  const qMax = resonanceOpts["domain/qMax"];
  return qMin + (resonance / resonanceParam.max) * (qMax - qMin);
}
function qToResonance(
  q: number,
  resonanceOpts: ResonanceQOpts,
  resonanceParam: { min: number; max: number },
): number {
  const qMin = resonanceOpts["domain/qMin"];
  const qMax = resonanceOpts["domain/qMax"];
  return clamp(
    ((q - qMin) / (qMax - qMin)) * resonanceParam.max,
    resonanceParam.min,
    resonanceParam.max,
  );
}

// The moog-filter frequency-response curve, in one component: pass `onChange` to get the
// fixed-size draggable editor (used standalone, e.g. by DatMoogFilter in @monit/tsuna); omit it
// for a read-only, scaled-to-fit preview (e.g. the compact row inline). Both are the same chart —
// see FreqResponseChart.ts — just with the handle enabled or not.

export interface MoogFilterCurveProps {
  params: ParamDict;
  /** Only `min`/`max` are read — pass the param descriptor itself (e.g. MoogFilterParams.cutoff). */
  cutoffParam: { min: number; max: number };
  /** Only `min`/`max` are read (`max` alone in read-only mode) — pass the param descriptor itself
   * (e.g. MoogFilterParams.resonance). */
  resonanceParam: { min: number; max: number };
  freqOpts?: Partial<FreqSvgOpts>;
  resonanceOpts?: Partial<ResonanceQOpts>;
  onChange?: (key: string, value: unknown) => void;
}

const HANDLE: FreqResponseHandleConfig = { color: "#4af", lock: "none" };

function initChart(container: HTMLDivElement, opts: FreqSvgOpts, editable: boolean) {
  return initFreqResponseChart(
    container,
    opts,
    "#4af",
    editable ? "editor" : "inline",
    editable,
    editable ? HANDLE : undefined,
  );
}

function updateChart(
  chart: FreqResponseChart,
  props: MoogFilterCurveProps,
  opts: FreqSvgOpts,
  resonanceOpts: ResonanceQOpts,
) {
  const editable = !!props.onChange;
  const width = editable ? opts["editor/width"] : opts["inline/virtualWidth"];
  const height = editable ? opts["editor/height"] : opts["inline/height"];
  const pad = editable ? opts["editor/pad"] : opts["inline/pad"];
  const qMin = resonanceOpts["domain/qMin"];
  const qMax = resonanceOpts["domain/qMax"];

  const cutoff = props.params.cutoff.value as number;
  const resonance = props.params.resonance.value as number;
  const freqHz = cutoffToHz(cutoff, opts);
  const qVal = resonanceToQ(resonance, resonanceOpts, props.resonanceParam);
  const path = buildResponsePath("lowpass", freqHz, qVal, width, height, pad, opts);
  const tx = makeTx(width, pad, opts["domain/fMin"], opts["domain/fMax"]);
  const fillPathD = editable
    ? `${path} L ${tx(opts["domain/fMax"]).toFixed(1)},${height - pad} L ${
      tx(opts["domain/fMin"]).toFixed(1)
    },${height - pad} Z`
    : undefined;

  updateFreqResponseChart(chart, path, {
    fillPathD,
    handle: editable
      ? {
        x: tx(freqHz),
        y: qToY(qVal, height, pad, qMin, qMax),
        label: freqHz >= 1000 ? `${(freqHz / 1000).toFixed(1)}k` : `${Math.round(freqHz)}`,
        onDrag: (x, y) => {
          const ft = makeFt(width, pad, opts["domain/fMin"], opts["domain/fMax"]);
          props.onChange!(
            "cutoff",
            parseFloat(hzToCutoff(ft(x), opts, props.cutoffParam).toFixed(4)),
          );
          props.onChange!(
            "resonance",
            parseFloat(
              qToResonance(yToQ(y, height, pad, qMin, qMax), resonanceOpts, props.resonanceParam)
                .toFixed(2),
            ),
          );
        },
      }
      : undefined,
  });
}

export function MoogFilterCurve(props: MoogFilterCurveProps) {
  const opts = { ...FreqSvgOptsDefaults, ...props.freqOpts };
  const resonanceOpts = { ...ResonanceQOptsDefaults, ...props.resonanceOpts };
  const editable = !!props.onChange;
  const containerRef = useD3Chart(
    (container) => initChart(container, opts, editable),
    (chart, p) => updateChart(chart, p, opts, resonanceOpts),
    props,
    [
      props.params.cutoff.value,
      props.params.resonance.value,
      props.cutoffParam.min,
      props.cutoffParam.max,
      props.resonanceParam.min,
      props.resonanceParam.max,
      editable,
    ],
  );
  return <div style={editable ? { background: "#0d0d0d" } : undefined} ref={containerRef} />;
}
